import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createProfileQueryKey } from '@/utils/queryKeys';
import type {
  CancelOrganizationSeatPackRemovalMutation,
  CancelOrganizationSeatPackRemovalMutationVariables,
} from './useCancelSeatPackRemoval.generated';

const query = gql`
  mutation CancelOrganizationSeatPackRemoval(
    $input: CancelOrganizationSeatPackRemovalInput!
  ) {
    cancelOrganizationSeatPackRemoval(input: $input)
  }
`;

// Cancels a pending end-of-period seat-pack decrease, keeping the current
// seat count.
export const useCancelSeatPackRemoval = (organizationId: string) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<
    CancelOrganizationSeatPackRemovalMutation,
    QueryError,
    CancelOrganizationSeatPackRemovalMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        CancelOrganizationSeatPackRemovalMutation,
        CancelOrganizationSeatPackRemovalMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: createProfileQueryKey() });
    },
  });

  const cancelSeatPackRemoval = (
    options?: MutateOptions<
      CancelOrganizationSeatPackRemovalMutation,
      QueryError,
      CancelOrganizationSeatPackRemovalMutationVariables
    >
  ) => {
    return mutate({ input: { organizationId } }, options);
  };

  return { cancelSeatPackRemoval, isPending };
};
